/** Tasks hooks. API data only when authenticated (errors → isError + empty). Client names resolved from the clients cache. */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { NOTIFICATIONS_QUERY_PREFIX, TASKS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Task, TaskStatus } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { useClients } from "@/features/clients/clients.hooks";
import {
  findInLists,
  patchInList,
  prependToList,
  removeFromList,
  restoreSnapshot,
  snapshotList,
} from "@/lib/optimistic-cache";
import { tasksApi } from "./tasks.api";
import type { ApiTask } from "./tasks.adapter";
import { toTask, toTaskWriteBody, type TaskInput } from "./tasks.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };
const ARCHIVED_PARAMS = {
  archived: true,
  limit: 100,
  sortBy: "createdAt",
  sortOrder: "desc" as const,
};

/** The two caches a task row can live in: the main list and "my tasks". */
function taskListKeys() {
  return [queryKeys.tasksList(LIST_PARAMS), queryKeys.myTasks];
}

/** Merge a server-confirmed task into both list caches + its detail cache. */
function patchTaskEverywhere(qc: QueryClient, id: string, task: ApiTask) {
  for (const key of taskListKeys()) patchInList<ApiTask>(qc, key, id, task);
  qc.setQueryData<ApiTask>(queryKeys.task(id), (old) => (old ? { ...old, ...task } : old));
}

export type DataSource = "api" | "mock";

export interface UseTasksResult {
  tasks: Task[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useTasks(): UseTasksResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.tasksList(LIST_PARAMS),
    queryFn: () => tasksApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data)
    return {
      tasks: query.data.data.map((t) => toTask(t, nameById)),
      isLoading: false,
      isError: false,
      isFetching: query.isFetching,
      source: "api",
      error: null,
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      tasks: [],
      isLoading: false,
      isError: true,
      isFetching: false,
      source: "api",
      error: query.error,
      refetch: query.refetch,
    };
  return {
    tasks: [],
    isLoading: query.isLoading,
    isError: false,
    isFetching: query.isFetching,
    source: "api",
    error: null,
    refetch: query.refetch,
  };
}

export function useMyTasks(): UseTasksResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.myTasks,
    queryFn: () => tasksApi.my(),
    enabled: authed,
  });

  if (query.data)
    return {
      tasks: query.data.data.map((t) => toTask(t, nameById)),
      isLoading: false,
      isError: false,
      isFetching: query.isFetching,
      source: "api",
      error: null,
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      tasks: [],
      isLoading: false,
      isError: true,
      isFetching: false,
      source: "api",
      error: query.error,
      refetch: query.refetch,
    };
  return {
    tasks: [],
    isLoading: query.isLoading,
    isError: false,
    isFetching: query.isFetching,
    source: "api",
    error: null,
    refetch: query.refetch,
  };
}

function useInvalidateTasks() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: TASKS_QUERY_PREFIX });
}

function useInvalidateNotifications() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_PREFIX });
}

export function useCreateTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: async (input: TaskInput) => {
      return tasksApi.create(toTaskWriteBody(input));
    },
    onSuccess: (data) => {
      prependToList<ApiTask>(qc, queryKeys.tasksList(LIST_PARAMS), data as ApiTask);
      void invalidate();
      void invalidateNotifications();
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: TaskInput }) => {
      return tasksApi.update(id, toTaskWriteBody(input));
    },
    onSuccess: (data, { id }) => {
      patchTaskEverywhere(qc, id, data as ApiTask);
      void invalidate();
      void invalidateNotifications();
    },
  });
}

export function useArchiveTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async (id: string) => {
      return tasksApi.archive(id);
    },
    // Optimistic: the task leaves both lists at once; rolled back on error.
    onMutate: async (id: string) => {
      const keys = taskListKeys();
      await Promise.all(keys.map((key) => qc.cancelQueries({ queryKey: key })));
      const snapshot = snapshotList(qc, keys);
      for (const key of keys) removeFromList<ApiTask>(qc, key, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}

/** Lightweight hook for member status-only updates. Optionally includes a progress override. */
export function useUpdateTaskStatus() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      progress,
    }: {
      id: string;
      status: TaskStatus;
      progress?: number;
    }) => {
      const body: Record<string, unknown> = { status };
      if (progress !== undefined) body.progress = progress;
      return tasksApi.update(id, body);
    },
    // The kanban card / status pill reflects the new status immediately.
    onSuccess: (data, { id }) => {
      patchTaskEverywhere(qc, id, data as ApiTask);
      void invalidate();
    },
  });
}

/** Sends only { checklist } — members may tick items on their own tasks. */
export function useUpdateChecklist() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({
      id,
      checklist,
    }: {
      id: string;
      checklist: Array<{ label: string; done: boolean }>;
    }) => {
      return tasksApi.update(id, { checklist });
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

/** Sends only { progress } — used by member inline progress input. */
export function useUpdateProgress() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({ id, progress }: { id: string; progress: number }) => {
      return tasksApi.update(id, { progress });
    },
    onSuccess: (data, { id }) => {
      patchTaskEverywhere(qc, id, data as ApiTask);
      void invalidate();
    },
  });
}

/** Spec §4.2 — "Démarrer": chrono starts server-side. */
export function useStartTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async (id: string) => {
      return tasksApi.start(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

/** Spec §4.2 — completion modal: DONE + note; the validator gets notified. */
export function useCompleteTask() {
  const invalidate = useInvalidateTasks();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: async ({
      id,
      note,
      files,
    }: {
      id: string;
      note?: string;
      files?: Array<{ name: string; mimeType?: string; dataBase64: string }>;
    }) => {
      return tasksApi.complete(id, { note, files });
    },
    onSuccess: () => {
      void invalidate();
      void invalidateNotifications();
    },
  });
}

/** Spec §4.2 — star rating 1-5 by the validator, stored for team performance. */
export function useRateTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({ id, rating }: { id: string; rating: number }) => {
      return tasksApi.rate(id, rating);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useRestoreTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async (id: string) => {
      return tasksApi.restore(id);
    },
    onMutate: async (id: string) => {
      const archivedKey = queryKeys.tasksList(ARCHIVED_PARAMS);
      await qc.cancelQueries({ queryKey: archivedKey });
      const snapshot = snapshotList(qc, [archivedKey]);
      removeFromList<ApiTask>(qc, archivedKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useArchivedTasks() {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);
  const query = useQuery({
    queryKey: queryKeys.tasksList(ARCHIVED_PARAMS),
    queryFn: () => tasksApi.list(ARCHIVED_PARAMS),
    enabled: authed,
  });
  if (query.data)
    return {
      tasks: query.data.data.map((t) => toTask(t, nameById)),
      isLoading: false,
      isError: false,
      refetch: query.refetch,
    };
  if (query.isError)
    return { tasks: [] as Task[], isLoading: false, isError: true, refetch: query.refetch };
  return {
    tasks: [] as Task[],
    isLoading: query.isLoading,
    isError: false,
    refetch: query.refetch,
  };
}

/** Warm the tasks caches (startup warmup — see layout/useWarmupCoreData). */
export function prefetchTasks(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.prefetchQuery({
      queryKey: queryKeys.tasksList(LIST_PARAMS),
      queryFn: () => tasksApi.list(LIST_PARAMS),
    }),
    qc.prefetchQuery({ queryKey: queryKeys.myTasks, queryFn: () => tasksApi.my() }),
  ]).then(() => undefined);
}

export function useTask(id: string): {
  task: Task | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const { clients } = useClients();
  const qc = useQueryClient();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.task(id),
    queryFn: () => tasksApi.findById(id),
    enabled: authed && !!id,
    // Instant paint: seed from the list row the user just clicked (all tasks or
    // "my tasks"). `initialDataUpdatedAt: 0` marks the seed stale so the real
    // fetch always follows and reconciles server-computed fields.
    initialData: () =>
      findInLists<ApiTask>(qc, [queryKeys.tasksList(LIST_PARAMS), queryKeys.myTasks], id),
    initialDataUpdatedAt: 0,
  });

  if (query.data)
    return { task: toTask(query.data, nameById), isLoading: false, isError: false, error: null };
  if (query.isError) return { task: null, isLoading: false, isError: true, error: query.error };
  return { task: null, isLoading: query.isLoading, isError: false, error: null };
}
